/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  Tooltip,
  makeStyles,
  Button,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { AiFillCloseCircle } from "react-icons/ai";
import { FiChevronDown } from "react-icons/fi";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(1.5),
  },
  small: {
    maxHeight: "35px",
    padding: "8px",
  },
  smallFont: {
    fontSize: "12px",
  },
  filterContainer: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    boxShadow: "0 1px 4px 0 rgba(37, 50, 56, 0.1)",
    borderRadius: "5px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    width: "100%",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#ECEFF1",
    },
  },
  inputTitleBox: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
  },
  alignCenterFlex: {
    display: "flex",
    alignItems: "center",
  },
  popoverRoot: {
    marginTop: "-5px",
    width: "100%",
    height: "550px",
  },
  listRoot: {
    padding: "0px",
    width: "100%",
  },
  listItem: {
    fontSize: "16px",
    minWidth: "300px",
    padding: "20px",
    "&::before": {
      content: `""`,
      position: "absolute",
      width: "calc(100% - 30px)",
      height: "1px",
      backgroundColor: "#ECEFF1",
      top: "0",
      left: "15px",
    },
    "&:hover": {
      backgroundColor: "#ECEFF1",
      cursor: "pointer",
    },
  },
  nested: {
    paddingLeft: "40px",
  },
  groupHeader: {
    width: "100%",
    fontWeight: "bold",
    padding: "16px",
    "&:hover": {
      backgroundColor: "#ECEFF1",
      cursor: "pointer",
    },
  },
  listGroup: {
    borderTop: "1px solid #ECEFF1",
    padding: "0px",
    width: "100%",
  },
  listGroupOnlyRetailerGroups: {
    borderTop: "1px solid #ECEFF1",
    fontSize: "16px",
    minWidth: "300px",
    padding: "3px",
    "&:hover": {
      backgroundColor: "#ECEFF1",
      cursor: "pointer",
    },
  },
  filterTitleAndIcon: {
    display: "flex",
    alignItems: "center",
    width: "calc(100% - 40px)",
    "& >:last-child": {
      marginLeft: "10px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      width: "calc(100% - 30px)",
      display: "inline-block",
    },
  },
  popoverTitle: {
    boxSizing: "border-box",
    display: "flex",
    padding: "20px",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 2,
    width: "100%",
    borderBottom: "1px solid #ECEFF1",
  },
  popoverTitleLabel: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  withFilterApplied: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: "#C45852",
      cursor: "pointer",
    },
  },
  dropdownRightIcon: {
    marginLeft: "15px",
  },
  disabledSelect: {
    backgroundColor: "#f0f0f0",
    color: "#b0b0b0",
    "&:hover": {
      cursor: "not-allowed",
      backgroundColor: "#f0f0f0",
    },
  },
  popoverCloseButton: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  listItemLabel: {
    userSelect: "none",
    pointerEvents: "none",
  },
}));

const FiltersNestedSelectComponent = ({
  inputData,
  disabled,
  filters,
  small,
  sort,
  handleChange,
  isPricingOrTpo,
  view = "grid",
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChildrenItems, setSelectedChildrenItems] = useState(new Map());
  const [selectedParentItems, setSelectedParentItems] = useState(new Map());
  const childrenRefs = useRef(new Map());
  const parentRefs = useRef(new Map());
  const filtersSlice = useSelector((state) => state.filters);

  const handleFilterclick = (event) => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // Function to handle clicking on children item
  const handleClickOnChildrenItem = (item) => {
    const refChildrenClassList = childrenRefs.current.get(item.id).classList;
    // Select the parent item
    const parentObj = item.categories
      ? item?.categories[0]
      : item?.retailer_groups;
    const parentId = parentObj.id;
    const refParentClassList = parentRefs.current.get(parentId).children[0]
      .children[0].classList;
    // Create an array of the children's ids
    const allChildrenIds = inputData.data.children.data[parentId].map(
      (children) => children.id
    );
    // Evaluate if all children from the parent id has been selected
    const allChildrenSelected = () =>
      _.every(allChildrenIds, (childrenId) => {
        return selectedChildrenItems.get(childrenId);
      });
    // Evaluate if some children from the parent id is not selected
    const someChildrenUnselected = () =>
      _.some(allChildrenIds, (childrenId) => {
        return !selectedChildrenItems.get(childrenId);
      });
    // At the moment of clicking the selected children may be or may not be selected
    if (selectedChildrenItems.has(item.id)) {
      // If selected, delete it from the group (it means it was unselected)
      if (selectedChildrenItems.delete(item.id)) {
        setSelectedChildrenItems(selectedChildrenItems);
        refChildrenClassList.remove(classes.withFilterApplied);
        // If the parent has some of it's children unselected,
        // should be removed from the parents group
        if (
          someChildrenUnselected() &&
          selectedParentItems.has(parentId) &&
          selectedParentItems.delete(parentId)
        ) {
          setSelectedParentItems(selectedParentItems);
          refParentClassList.remove(classes.withFilterApplied);
        }
      }
    } else {
      // If it is not selected, add it to the group (it means it was selected)
      setSelectedChildrenItems(selectedChildrenItems.set(item.id, item));
      refChildrenClassList.add(classes.withFilterApplied);
      // If the parent has all of it's children selected,
      // should be added to the parents group
      if (allChildrenSelected() && !selectedParentItems.has(parentId)) {
        setSelectedParentItems(selectedParentItems.set(parentId, parentObj));
        refParentClassList.add(classes.withFilterApplied);
      }
    }
  };

  // Function to handle clicking on parent item
  const handleClickOnParentItem = (item) => {
    const refParentClassList = parentRefs.current.get(item.id).children[0]
      .children[0].classList;
    const refParentChildrenArray = parentRefs.current.get(item.id).children[0]
      .children[1].children;
    // When selecting a parent it may or may not be selected
    if (selectedParentItems.has(item.id)) {
      // If selected, the parent should be removed (it was unselected)
      if (selectedParentItems.delete(item.id)) {
        setSelectedParentItems(selectedParentItems);
        refParentClassList.remove(classes.withFilterApplied);
        // Iterate through all children of the selected parent,
        // and remove those who are selected because the parent has been removed
        _.forEach(inputData.data.children.data[item.id], (childrenItem) => {
          if (
            selectedChildrenItems.has(childrenItem.id) &&
            selectedChildrenItems.delete(childrenItem.id)
          ) {
            setSelectedChildrenItems(selectedChildrenItems);
            const refChildElement = Array.from(refParentChildrenArray).find(
              (refChildren) => refChildren.outerText === childrenItem.label
            );
            refChildElement.classList.remove(classes.withFilterApplied);
          }
        });
      }
    } else {
      // If it is not selected, add the parent to the group
      setSelectedParentItems(selectedParentItems.set(item.id, item));
      refParentClassList.add(classes.withFilterApplied);
      // parentRefs.current.get(item.id).classList.add(classes.withFilterApplied);
      // And add all the children that are not selected yet
      _.forEach(inputData.data.children.data[item.id], (childrenItem) => {
        if (!selectedChildrenItems.has(childrenItem.id)) {
          setSelectedChildrenItems(
            selectedChildrenItems.set(childrenItem.id, childrenItem)
          );
          const refChildElement = Array.from(refParentChildrenArray).find(
            (refChildren) => refChildren.outerText === childrenItem.label
          );
          refChildElement.classList.add(classes.withFilterApplied);
        }
      });
    }
  };

  const handleChildrenListItemClick = (event, value) => {
    event.stopPropagation();
    handleClickOnChildrenItem(value);
  };

  const handleParentListItemClick = (event, value) => {
    event.stopPropagation();
    handleClickOnParentItem(value);
  };

  const handleRemoveFilterClick = (event) => {
    if (event) event.stopPropagation();
    setSelectedChildrenItems(new Map());
    setSelectedParentItems(new Map());
    handleChange({
      [inputData.data.parent.key]: "",
      [inputData.data.children.key]: "",
    });
    setAnchorEl(null);
  };

  const handleOnClose = () => {
    const childrenArray = Array.from(selectedChildrenItems.values());
    const parentArray = Array.from(selectedParentItems.values());
    const filtersChildrenChanged =
      JSON.stringify(filters[inputData.data.children.key] || []) !==
      JSON.stringify(childrenArray);
    const filtersParentChanged =
      JSON.stringify(filters[inputData.data.parent.key] || []) !==
      JSON.stringify(parentArray);
    if (filtersChildrenChanged || filtersParentChanged) {
      handleChange({
        [inputData.data.children.key]: _.isEmpty(childrenArray)
          ? ""
          : childrenArray,
        [inputData.data.parent.key]: _.isEmpty(parentArray) ? "" : parentArray,
      });
    }
    setAnchorEl(null);
  };

  const isSelectedKey = !!(
    filters[inputData.data.parent.key] || filters[inputData.data.children.key]
  );

  // Reset retailer filter in certain conditions
  useEffect(() => {
    if (inputData.key === "retailers" && view === "summary") {
      handleRemoveFilterClick();
    }
  }, [filters.category, filters.product]);

  return (
    <>
      <div
        role="presentation"
        onClick={handleFilterclick}
        className={`${classes.filterContainer} ${classes.padding} 
        ${disabled && classes.disabledSelect} ${isSelectedKey &&
          classes.withFilterApplied} ${small && classes.small} ${small &&
          classes.smallFont}`}
      >
        <Tooltip
          title={
            isSelectedKey
              ? Array.from(selectedChildrenItems.values())
                  .map((item) => item.label)
                  .toString()
                  .replace(/,/g, ", ")
              : inputData.label
          }
          placement="right"
          data-testid="filterNestedTooltip"
        >
          <Box component="div" className={classes.inputTitleBox}>
            <Box component="span" className={classes.filterTitleAndIcon}>
              {inputData.icon}
              <Typography
                variant="body1"
                className={`${classes.listItemLabel} ${
                  classes.alignCenterFlex
                } ${small && classes.smallFont}`}
                data-testid="filterNestedText"
              >
                {inputData.label}
              </Typography>
            </Box>
            {isSelectedKey ? (
              <AiFillCloseCircle
                className={classes.dropdownRightIcon}
                color="#fff"
                size={24}
                onClick={(event) => handleRemoveFilterClick(event)}
              />
            ) : (
              <FiChevronDown
                className={classes.dropdownRightIcon}
                size={18}
                data-testid="filtersArrowIcon"
              />
            )}
          </Box>
        </Tooltip>
      </div>
      <Popover
        className={classes.popoverRoot}
        open={Boolean(anchorEl)}
        onClose={handleOnClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        data-testid="filterNestedPopover"
      >
        <Box component="div" className={`${classes.popoverTitle}`}>
          <div className={classes.popoverTitleLabel}>
            {inputData.icon}
            <Typography
              variant="body1"
              className={`${classes.listItemLabel} ${
                classes.alignCenterFlex
              } ${small && classes.smallFont}`}
              data-testid="filterListTitle"
            >
              <b>{inputData.label}</b>
            </Typography>
          </div>
          <Button
            className={`${classes.popoverCloseButton} ${small &&
              classes.smallFont} `}
            onClick={handleOnClose}
          >
            APPLY
          </Button>
        </Box>

        <List className={classes.listRoot}>
          {_.map(
            sort
              ? _.orderBy(inputData.data.parent.data, "label", sort)
              : inputData.data.parent.data,
            (parentItem) => {
              return (
                <ListItem
                  key={parentItem.id}
                  ref={(element) =>
                    parentRefs.current.set(parentItem.id, element)
                  }
                  onClick={(event) =>
                    handleParentListItemClick(event, parentItem)
                  }
                  className={`${classes.listGroup} ${small &&
                    classes.smallFont}`}
                >
                  <div role="presentation" className={`${classes.listGroup}`}>
                    <Typography
                      className={`${
                        classes.groupHeader
                      }  ${selectedParentItems.has(parentItem.id) &&
                        classes.withFilterApplied}
                        ${small && classes.smallFont}
                      `}
                    >
                      {parentItem.label}
                    </Typography>
                    <List className={classes.listRoot}>
                      {filtersSlice.groupBy.key === "retailerGroup" &&
                      isPricingOrTpo
                        ? ""
                        : _.map(
                            sort
                              ? _.orderBy(
                                  inputData.data.children.data[parentItem.id],
                                  "label",
                                  sort
                                )
                              : inputData.data.children.data[parentItem.id],
                            (childrenItem) => {
                              return (
                                <ListItem
                                  key={childrenItem.id}
                                  ref={(element) =>
                                    childrenRefs.current.set(
                                      childrenItem.id,
                                      element
                                    )
                                  }
                                  onClick={(event) =>
                                    handleChildrenListItemClick(
                                      event,
                                      childrenItem
                                    )
                                  }
                                  className={`${classes.listItem} ${
                                    classes.nested
                                  } ${selectedChildrenItems.has(
                                    childrenItem.id
                                  ) && classes.withFilterApplied} ${small &&
                                    classes.smallFont}`}
                                >
                                  <Typography
                                    className={`${
                                      classes.listItemLabel
                                    } ${small && classes.smallFont}`}
                                  >
                                    {childrenItem.label}
                                  </Typography>
                                </ListItem>
                              );
                            }
                          )}
                    </List>
                  </div>
                </ListItem>
              );
            }
          )}
        </List>
      </Popover>
    </>
  );
};

export default FiltersNestedSelectComponent;
